import { AppNode } from "@features/builder/graph/types"
import { useGraphDocument } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocument"
import { GraphDetails } from "@features/builder/types"
import {
  useGetGraphDashboardFileQuery,
  usePostDashboardFileMutation,
} from "@store/services/dashboardService"
import { stringToFile } from "@utils/file"
import { createNodeLabelMap } from "@utils/node"
import { useNodes } from "@xyflow/react"
import { enqueueSnackbar } from "notistack"
import { updateGraphDocumentData } from "@utils/crdt"
import { useEffect, useState } from "react"
import logger from "@utils/logger"
import ValidationErrorsSnackbar from "@features/builder/graph/validation/ValidationErrorsSnackbar"
import {
  DashboardConfigurationDialogProps,
  DashboardConfigurationFormValues,
} from "@features/builder/DashboardConfiguration/types"
import { DashboardConfigurationDialog } from "@features/builder/DashboardConfiguration/DashboardConfigurationDialog"
import { DashboardConfigurationForm } from "@features/builder/DashboardConfiguration/DashboardConfigurationForm"
import { FormProvider, SubmitErrorHandler, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { validationSchema } from "@features/builder/DashboardConfiguration/schema"

export const FORM_ID = "dashboard-configuration-form"

const getDefaultValues = (fileInString: string | undefined): { configuration: string } => {
  if (!fileInString) return { configuration: "" }

  return { configuration: fileInString }
}

export const DashboardConfiguration = (props: DashboardConfigurationDialogProps) => {
  const allNodes = useNodes<AppNode>()
  const [postDashboardFile] = usePostDashboardFileMutation()
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const graphDocument = useGraphDocument()
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)

  const { data: fetchedDashboard } = useGetGraphDashboardFileQuery(props.graphKey)
  const form = useForm<DashboardConfigurationFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: getDefaultValues(fetchedDashboard),
  })

  useEffect(() => {
    form.reset(getDefaultValues(fetchedDashboard))
  }, [fetchedDashboard, form])

  const handleErrors: SubmitErrorHandler<DashboardConfigurationFormValues> = (errors) => {
    const error = errors.configuration?.message

    enqueueSnackbar("Dashboard errors", {
      persist: true,
      anchorOrigin: { horizontal: "right", vertical: "top" },
      content: (key) => (
        <ValidationErrorsSnackbar
          id={key}
          errors={["Invalid YAML: \n" + error]}
          title="Dashboard Errors"
        />
      ),
    })
  }

  const handleSubmit = async (values: DashboardConfigurationFormValues) => {
    if (!graphDocument) return

    const labelMap = Object.values(createNodeLabelMap(allNodes))

    const file = stringToFile("dashboard.yaml", values.configuration)
    const graphData = JSON.parse(
      graphDocument.getMap("graph").get("data")?.toJSON() ?? "null",
    ) as GraphDetails | null

    if (!graphData) {
      logger.error(`Graph data does not exist on graph "${props.graphKey}"`)
      return
    }

    try {
      const response = await postDashboardFile({
        data: file,
        graph_id: props.graphKey,
        nodes: labelMap,
      }).unwrap()

      if (!("dashboard_hash" in response)) return

      graphData.DashboardS3Location = response.path
      graphData.DashboardS3Hash = response.dashboard_hash
      updateGraphDocumentData(graphDocument, graphData)
      enqueueSnackbar({ variant: "success", message: "Dashboard saved successfully" })
      setErrorMessages([])
    } catch (e) {
      const errors = (e as { data: { message: string } }).data.message.split("\n")
      setErrorMessages(errors)

      enqueueSnackbar("Dashboard errors", {
        persist: true,
        anchorOrigin: { horizontal: "right", vertical: "top" },
        content: (key) => (
          <ValidationErrorsSnackbar
            id={key}
            errors={errors.map((msg) => msg.split("DASHBOARD_DOCUMENT_ERROR:")[1] ?? "")}
            title="Dashboard Errors"
          />
        ),
      })
    }
    return
  }

  const handleClear = async () => {
    setIsDeleteOpen(true)
  }

  return (
    <FormProvider {...form}>
      <DashboardConfigurationDialog
        graphKey={props.graphKey}
        onClose={props.onClose}
        isDeleteOpen={isDeleteOpen}
        onIsDeleteOpen={setIsDeleteOpen}
      >
        <DashboardConfigurationForm
          onClear={handleClear}
          onSubmit={handleSubmit}
          onSubmitError={handleErrors}
          graphKey={props.graphKey}
          errorMessages={errorMessages}
        />
      </DashboardConfigurationDialog>
    </FormProvider>
  )
}
